import { Card, CardContent, Skeleton, cn } from '@yeshuman/sdk'

export type HealthListPageSkeletonProps = {
  count?: number
  /** Include filter pill-bar stub above the list. */
  showFilters?: boolean
  ariaLabel?: string
  className?: string
}

function HealthCardRowSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-3 p-[var(--sp-card)]">
        <div className="flex items-start justify-between gap-3">
          <Skeleton className="h-5 w-2/5 max-w-[12rem]" />
          <Skeleton className="h-5 w-16 rounded-md" />
        </div>
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
        <div className="flex gap-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-24" />
        </div>
      </CardContent>
    </Card>
  )
}

export function HealthListPageSkeleton({
  count = 4,
  showFilters = true,
  ariaLabel = 'Loading health records',
  className,
}: HealthListPageSkeletonProps) {
  return (
    <div
      className={cn('space-y-6', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      {showFilters ? (
        <div className="flex flex-wrap items-center gap-2">
          <Skeleton className="h-6.5 w-24 rounded-[var(--radius-button)]" />
          <Skeleton className="h-6.5 w-32 rounded-[var(--radius-button)]" />
          <Skeleton className="h-6.5 w-20 rounded-[var(--radius-button)]" />
        </div>
      ) : null}
      <div className="space-y-4">
        {Array.from({ length: count }).map((_, index) => (
          <HealthCardRowSkeleton key={index} />
        ))}
      </div>
    </div>
  )
}

/** Nutrition page: totals strip + meal rows. */
export function NutritionPageSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('space-y-6', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Loading nutrition"
    >
      <div className="grid grid-cols-2 gap-3 @2col:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-16 w-full rounded-lg" />
        ))}
      </div>
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    </div>
  )
}
